import { HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { map, of, switchMap } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { LocalStorageService } from '../../@core/services/local-storage.service';
import { ApiErrorType } from '../app.actions';

import { ClientsApiActions } from './clients.actions';
import { ClientsService } from './clients.service';
import { Client, Country, State } from './clients.state.interface';

export const loadClients = createEffect(
  (actions$ = inject(Actions), clientsService = inject(ClientsService)) =>
    actions$.pipe(
      ofType(ClientsApiActions.loadClients),
      switchMap(({ payload }) =>
        clientsService.getClients(payload).pipe(
          map((response: HttpResponse<Client[]>) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const clients = response.body || [];

            return ClientsApiActions.loadClientsSuccess({ clients, totalCount: +totalCount });
          }),
          catchError((error: ApiErrorType) => of(ClientsApiActions.loadClientsError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const addClient = createEffect(
  (actions$ = inject(Actions), clientsService = inject(ClientsService)) =>
    actions$.pipe(
      ofType(ClientsApiActions.addClient),
      switchMap(({ payload }) =>
        clientsService.addClient(payload).pipe(
          map((client: Client) => ClientsApiActions.addClientSuccess({ client })),
          catchError((error: ApiErrorType) => of(ClientsApiActions.addClientError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const addClientSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(ClientsApiActions.addClientSuccess),
      tap(() => {
        router.navigate([`/clients`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

export const addClientError = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(ClientsApiActions.addClientError),
      tap(({ error }) => {
        const errorMessage = (error?.error?.errors as string) || 'An error occurred';

        snackBar.open(errorMessage, 'Close', {
          duration: 6000,
          panelClass: ['snackbar-error', 'whitespace-pre-line'],
          horizontalPosition: 'end',
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const loadClient = createEffect(
  (actions$ = inject(Actions), clientsService = inject(ClientsService)) =>
    actions$.pipe(
      ofType(ClientsApiActions.loadClient),
      switchMap(({ clientId }) =>
        clientsService.getClient(clientId).pipe(
          map((client: Client) => ClientsApiActions.loadClientSuccess({ client })),
          catchError((error: ApiErrorType) => of(ClientsApiActions.loadClientError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateClient = createEffect(
  (actions$ = inject(Actions), clientsService = inject(ClientsService)) =>
    actions$.pipe(
      ofType(ClientsApiActions.updateClient),
      switchMap(({ payload, clientId }) =>
        clientsService.updateClient(payload, clientId).pipe(
          map((client: Client) => ClientsApiActions.updateClientSuccess({ client })),
          catchError((error: ApiErrorType) => of(ClientsApiActions.updateClientError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateClientSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(ClientsApiActions.updateClientSuccess),
      tap(() => {
        router.navigate([`/clients`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

export const duplicateClient = createEffect(
  (
    actions$ = inject(Actions),
    clientsService = inject(ClientsService),
    localStorageService = inject(LocalStorageService),
  ) =>
    actions$.pipe(
      ofType(ClientsApiActions.duplicateClient),
      switchMap(({ clientId }) =>
        clientsService.duplicateClient(clientId).pipe(
          map((client: Client) => {
            localStorageService.setItem('duplicatedClient', JSON.stringify(client));

            return ClientsApiActions.duplicateClientSuccess({ client });
          }),
          catchError((error: ApiErrorType) =>
            of(ClientsApiActions.duplicateClientError({ error })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const duplicateClientSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(ClientsApiActions.duplicateClientSuccess),
      tap(() => {
        const clientId = router.url.split('/')[3];

        router.navigate([`/clients/edit/${clientId}/duplicate`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

export const loadCountries = createEffect(
  (actions$ = inject(Actions), clientsService = inject(ClientsService)) =>
    actions$.pipe(
      ofType(ClientsApiActions.loadCountries),
      switchMap(({ payload }) =>
        clientsService.getCountries(payload).pipe(
          map((countries: Country[]) => ClientsApiActions.loadCountriesSuccess({ countries })),
          catchError((error: ApiErrorType) => of(ClientsApiActions.loadCountriesError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadStates = createEffect(
  (actions$ = inject(Actions), clientsService = inject(ClientsService)) =>
    actions$.pipe(
      ofType(ClientsApiActions.loadStatesForAddress1),
      switchMap(({ payload }) =>
        clientsService.getStates(payload).pipe(
          map((states: State[]) => ClientsApiActions.loadStatesForAddress1Success({ states })),
          catchError((error: ApiErrorType) =>
            of(ClientsApiActions.loadStatesForAddress1Error({ error })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadStatesForAddress2 = createEffect(
  (actions$ = inject(Actions), clientsService = inject(ClientsService)) =>
    actions$.pipe(
      ofType(ClientsApiActions.loadStatesForAddress2),
      switchMap(({ payload }) =>
        clientsService.getStates(payload).pipe(
          map((states: State[]) => ClientsApiActions.loadStatesForAddress2Success({ states })),
          catchError((error: ApiErrorType) =>
            of(ClientsApiActions.loadStatesForAddress2Error({ error })),
          ),
        ),
      ),
    ),
  { functional: true },
);
